import { useCustom, useTranslate } from '@refinedev/core';
import { baseEmotionsEn, baseEmotionsRu } from '@cc/schema';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Input,
  InputNumber,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { weekdayToken } from '../../utils/weekday';
import { useMemo, useState } from 'react';
import { CardEditor } from './CardEditor';
import {
  WEEK_FIELDS,
  TEMPLATE_FIELDS,
  blankDraft,
  cellOf,
  defineInputOf,
  draftFromCard,
  draftFromTemplate,
  draftProblem,
  emotionLabel,
  emptyFill,
  fillInputOf,
  fillableDays,
  isHighlighted,
  newId,
  rowLabel,
  scaleValues,
  shiftWeek,
  weekDays,
  type CardDraft,
  type FillDraft,
  type RowValue,
  type Target,
  type TemplateRow,
  type WeekGrid,
  type WeekRow,
} from './model';
import { useDiaryCardCommand } from './useDiaryCardCommand';

/**
 * E37 · DiaryCardWeek (portal, buildOrder 18) + DefineDiaryCard (a practice's card, 11) + FillDiaryCardInSession (19) —
 * "Client Hub — Diary card": the week grid for the session — Linehan's review order: the target rows and the highlights
 * first, then the emotions, then the skills. It shows THIS practice's card; the client's own card appears read-only beneath
 * it, marked "set up by the client" (the hub visibility rule of E07, like the check-ins) — never another practice's card.
 * A day the therapist filled in session is marked "filled in session". Set up (or edit) THIS practice's card here; a new
 * save is a new version of the same card. Fill in session: the therapist fills THEIR practice's card for the client
 * (telehealth with a shared screen, a client with no phone), today or up to 7 days back; it is the practice's record, emits
 * no client usage and never completes an assigned homework. Nothing here reads a rating and acts on it; the standing
 * safety-plan link waits for E38.
 */
type Props = {
  clientId: string;
  clientName: string;
  locale: 'en' | 'ru';
};

type T = (key: string, options?: Record<string, unknown>) => string;

export const ClientDiaryCardTab = ({ clientId, clientName, locale }: Props) => {
  const t = useTranslate() as unknown as T;
  const dateLocale = locale === 'ru' ? ru : enUS;
  const [weekOf, setWeekOf] = useState<string | null>(null);
  const [mode, setMode] = useState<'view' | 'setup' | 'fill'>('view');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [draft, setDraft] = useState<CardDraft>(blankDraft());
  const [fill, setFill] = useState<FillDraft>(() => emptyFill(fillableDays(new Date())[0]));
  const [fillId, setFillId] = useState(() => newId());
  const { run, isLoading } = useDiaryCardCommand();

  const { data, isFetching, isError, refetch } = useCustom<WeekRow>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'diaryCardWeek',
      fields: WEEK_FIELDS,
      variables: {
        subject: { name: 'subject', type: 'ID', value: clientId },
        weekOf: { name: 'weekOf', type: 'String', value: weekOf },
      },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const { data: tplData } = useCustom<TemplateRow[]>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'diaryCardTemplates',
      fields: TEMPLATE_FIELDS,
      variables: { locale: { name: 'locale', type: 'String', value: locale } },
    },
    queryOptions: { enabled: mode === 'setup', retry: false },
  });
  const templates = tplData?.data ?? [];

  const week = data?.data;
  const monday = week?.weekOf ?? weekOf;
  const days = useMemo(() => (monday ? weekDays(monday) : []), [monday]);
  const practiceCard = week?.cards.find((c) => c.setUpBy === 'practice') ?? null;

  const dayLabel = (d: string) => format(parseISO(d), `${weekdayToken(locale)} d MMM`, { locale: dateLocale });
  const emotionName = (id: number) => {
    const groups = locale === 'ru' ? baseEmotionsRu : baseEmotionsEn;
    for (const g of groups) {
      if (g.id === id) return g.name;
      const hit = g.children.find((c) => c.id === id);
      if (hit) return hit.name;
    }
    return String(id);
  };

  const openSetup = () => {
    setMessage(null);
    setDraft(practiceCard ? draftFromCard(practiceCard) : blankDraft());
    setMode('setup');
  };
  const openFill = () => {
    setMessage(null);
    setFill(emptyFill(fillableDays(new Date())[0]));
    setFillId(newId());
    setMode('fill');
  };

  const onSaveCard = async () => {
    const res = await run('defineDiaryCard', defineInputOf(clientId, draft), t('diaryCard.hub.saveFailed'));
    if (res.ok) {
      setMessage({ type: 'success', text: t('diaryCard.hub.cardSaved') });
      setMode('view');
      refetch();
    } else {
      setMessage({ type: 'error', text: res.message });
    }
  };

  const onSaveFill = async () => {
    if (!practiceCard) return;
    const input = fillInputOf(clientId, fillId, practiceCard, fill);
    if (!input) return;
    const res = await run('fillDiaryCardInSession', input, t('diaryCard.session.saveFailed'));
    if (res.ok) {
      setMessage({ type: 'success', text: t('diaryCard.session.saved') });
      setMode('view');
      setFillId(newId());
      refetch();
    } else {
      setMessage({ type: 'error', text: res.message });
    }
  };

  const grid = (card: WeekGrid) => {
    const byDay = new Map(card.dayEntries.map((e) => [e.day, e]));
    const hasSkills = card.skills.length > 0;
    type Row = { day: string };
    return (
      <Card
        key={card.cardId}
        size="small"
        title={
          card.setUpBy === 'client'
            ? t('diaryCard.hub.clientCardTitle', { name: clientName })
            : t('diaryCard.hub.practiceCardTitle')
        }
        extra={
          card.setUpBy === 'client' ? (
            <Tag data-testid={`diary-hub-readonly-${card.cardId}`}>
              {t('diaryCard.hub.readOnly')}
            </Tag>
          ) : null
        }
        data-testid={`diary-hub-grid-${card.cardId}`}
      >
        <Table<Row>
          dataSource={days.map((day) => ({ day }))}
          pagination={false}
          rowKey="day"
          size="small"
          scroll={{ x: true }}
          columns={[
            {
              title: t('diaryCard.hub.day'),
              dataIndex: 'day',
              render: (day: string) => (
                <Space direction="vertical" size={0}>
                  <span>{dayLabel(day)}</span>
                  {card.inSessionDays.includes(day) ? (
                    <Tag color="blue" data-testid={`diary-hub-in-session-${card.cardId}-${day}`}>
                      {t('diaryCard.hub.filledInSession')}
                    </Tag>
                  ) : null}
                </Space>
              ),
            },
            ...card.targets.map((r: Target) => ({
              title: rowLabel(t, r.targetId, r.name),
              key: r.targetId,
              render: (_: unknown, row: Row) => {
                const hit = isHighlighted(card.highlights, row.day, r.targetId);
                return (
                  <span
                    data-testid={`diary-hub-cell-${card.cardId}-${row.day}-${r.targetId}`}
                    style={hit ? { fontWeight: 700, background: '#ffe7c2', padding: '0 6px' } : undefined}
                  >
                    {cellOf(byDay.get(row.day)?.ratings.find((x) => x.targetId === r.targetId))}
                    {hit ? (
                      <span
                        data-testid={`diary-hub-highlight-${card.cardId}-${row.day}-${r.targetId}`}
                      />
                    ) : null}
                  </span>
                );
              },
            })),
            ...card.emotions.map((e) => ({
              title: emotionLabel(t, e.id, emotionName),
              key: `e-${e.id}`,
              render: (_: unknown, row: Row) =>
                byDay.get(row.day)?.emotionRatings.find((x) => x.id === e.id)?.rating ?? '',
            })),
            ...(hasSkills
              ? [
                  {
                    title: t('diaryCard.hub.skillsUsed'),
                    key: 'skillsUsed',
                    render: (_: unknown, row: Row) => byDay.get(row.day)?.skillsUsed ?? '',
                  },
                ]
              : []),
          ]}
        />
      </Card>
    );
  };

  const setFillValue = (key: string, value: RowValue) =>
    setFill((f) => ({ ...f, values: { ...f.values, [key]: value } }));

  const fillRow = (r: Target) => {
    const value = fill.values[r.targetId] ?? null;
    const id = `diary-fill-${r.targetId}`;
    return (
      <div key={r.targetId} data-testid={`diary-fill-row-${r.targetId}`}>
        <div>{rowLabel(t, r.targetId, r.name)}</div>
        {r.kind === 'urge' || r.kind === 'scale' ? (
          <Select
            allowClear
            data-testid={id}
            onChange={(v) => setFillValue(r.targetId, v ?? null)}
            options={scaleValues(r.max).map((v) => ({ value: v, label: String(v) }))}
            style={{ width: 120 }}
            value={typeof value === 'number' ? value : undefined}
          />
        ) : r.kind === 'action' && !r.countable ? (
          <Select
            allowClear
            data-testid={id}
            onChange={(v) => setFillValue(r.targetId, v ?? null)}
            options={[
              { value: true, label: t('diaryCard.hub.yes') },
              { value: false, label: t('diaryCard.hub.no') },
            ]}
            style={{ width: 120 }}
            value={typeof value === 'boolean' ? value : undefined}
          />
        ) : r.kind === 'text' ? (
          <Input
            data-testid={id}
            onChange={(e) => setFillValue(r.targetId, e.target.value || null)}
            value={typeof value === 'string' ? value : ''}
          />
        ) : (
          <InputNumber
            data-testid={id}
            min={0}
            onChange={(v) => setFillValue(r.targetId, v === null ? null : Number(v))}
            precision={0}
            value={typeof value === 'number' ? value : null}
          />
        )}
      </div>
    );
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }} data-testid="diary-hub-tab">
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {t('diaryCard.hub.title')}
        </Typography.Title>
        <Space wrap>
          <Button
            data-testid="diary-hub-prev"
            onClick={() => monday && setWeekOf(shiftWeek(monday, -1))}
          >
            {t('diaryCard.hub.previousWeek')}
          </Button>
          <Typography.Text type="secondary" data-testid="diary-hub-week">
            {monday ? `${dayLabel(monday)} – ${dayLabel(days[6] ?? monday)}` : ''}
          </Typography.Text>
          <Button
            data-testid="diary-hub-next"
            onClick={() => monday && setWeekOf(shiftWeek(monday, 1))}
          >
            {t('diaryCard.hub.nextWeek')}
          </Button>
          <Button
            data-testid="diary-hub-setup"
            disabled={!week}
            onClick={openSetup}
            type={practiceCard ? 'default' : 'primary'}
          >
            {practiceCard ? t('diaryCard.hub.editCard') : t('diaryCard.hub.setUpCard')}
          </Button>
          <Button
            data-testid="diary-hub-fill"
            disabled={!practiceCard}
            onClick={openFill}
            type="primary"
          >
            {t('diaryCard.session.fillButton')}
          </Button>
        </Space>
      </Space>

      {message ? (
        <Alert
          type={message.type}
          showIcon
          message={message.text}
          data-testid={`diary-hub-${message.type}`}
        />
      ) : null}
      {isError ? <Alert type="error" showIcon message={t('diaryCard.hub.loadError')} /> : null}

      {mode === 'setup' ? (
        <Card
          title={
            practiceCard
              ? t('diaryCard.hub.editTitle', { name: clientName })
              : t('diaryCard.hub.setUpTitle', { name: clientName })
          }
          data-testid="diary-hub-setup-form"
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Typography.Paragraph type="secondary">
              {t('diaryCard.hub.setUpHint')}
            </Typography.Paragraph>
            {!practiceCard ? (
              <Select
                allowClear
                data-testid="diary-hub-template"
                onChange={(id) => {
                  const tpl = templates.find((x) => x.templateId === id);
                  setDraft(tpl ? draftFromTemplate(tpl) : blankDraft());
                }}
                options={templates.map((x) => ({ value: x.templateId, label: x.name }))}
                placeholder={t('diaryCard.hub.startFrom')}
                style={{ width: 320 }}
              />
            ) : null}
            <CardEditor draft={draft} idPrefix="diary-editor" locale={locale} onChange={setDraft} />
            {draftProblem(draft) ? (
              <Typography.Text type="secondary">
                {draftProblem(draft) === 'empty'
                  ? t('diaryCard.hub.needRows')
                  : t('diaryCard.hub.needNames')}
              </Typography.Text>
            ) : null}
            <Space>
              <Button
                data-testid="diary-hub-save-card"
                disabled={!!draftProblem(draft)}
                loading={isLoading}
                onClick={onSaveCard}
                type="primary"
              >
                {t('diaryCard.hub.saveCard')}
              </Button>
              <Button onClick={() => setMode('view')}>{t('diaryCard.hub.cancel')}</Button>
            </Space>
          </Space>
        </Card>
      ) : null}

      {mode === 'fill' && practiceCard ? (
        <Card
          title={t('diaryCard.session.fillTitle', { name: clientName })}
          data-testid="diary-session-form"
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Typography.Paragraph type="secondary">
              {t('diaryCard.session.fillHint')}
            </Typography.Paragraph>
            <div>
              <div>{t('diaryCard.session.day')}</div>
              <Select
                data-testid="diary-session-day"
                onChange={(day) => setFill((f) => ({ ...f, day }))}
                options={fillableDays(new Date()).map((d) => ({ value: d, label: dayLabel(d) }))}
                style={{ width: 200 }}
                value={fill.day}
              />
            </div>
            {practiceCard.targets.map(fillRow)}
            {practiceCard.emotions.length ? (
              <div>
                <div>{t('diaryCard.hub.emotions')}</div>
                {practiceCard.emotions.map((e) => (
                  <div key={e.id}>
                    <span>{emotionLabel(t, e.id, emotionName)}</span>{' '}
                    <Select
                      allowClear
                      data-testid={`diary-session-emotion-${e.id}`}
                      onChange={(v) =>
                        setFill((f) => {
                          const next = { ...f.emotions };
                          if (v === undefined || v === null) delete next[e.id];
                          else next[e.id] = v;
                          return { ...f, emotions: next };
                        })
                      }
                      options={scaleValues(5).map((v) => ({ value: v, label: String(v) }))}
                      style={{ width: 100 }}
                      value={fill.emotions[e.id]}
                    />
                  </div>
                ))}
              </div>
            ) : null}
            {practiceCard.skills.length ? (
              <div>
                <div>{t('diaryCard.session.skillsUsed')}</div>
                <Select
                  allowClear
                  data-testid="diary-session-skills-used"
                  onChange={(v) => setFill((f) => ({ ...f, skillsUsed: v ?? null }))}
                  options={scaleValues(7).map((v) => ({ value: v, label: String(v) }))}
                  style={{ width: 100 }}
                  value={fill.skillsUsed ?? undefined}
                />
                <div>{t('diaryCard.session.skillsPracticed')}</div>
                <Checkbox.Group
                  onChange={(v) => setFill((f) => ({ ...f, skillsPracticed: v as string[] }))}
                  options={practiceCard.skills.map((s) => ({
                    value: s.skillId,
                    label: t(`diaryCard.skill.${s.skillId}`),
                  }))}
                  value={fill.skillsPracticed}
                />
              </div>
            ) : null}
            <div>
              <div>{t('diaryCard.session.note')}</div>
              <Input.TextArea
                data-testid="diary-session-note"
                maxLength={4000}
                onChange={(e) => setFill((f) => ({ ...f, note: e.target.value }))}
                rows={3}
                value={fill.note}
              />
            </div>
            <Space>
              <Button
                data-testid="diary-session-save"
                disabled={!fillInputOf(clientId, fillId, practiceCard, fill)}
                loading={isLoading}
                onClick={onSaveFill}
                type="primary"
              >
                {t('diaryCard.session.save')}
              </Button>
              <Button onClick={() => setMode('view')}>{t('diaryCard.hub.cancel')}</Button>
            </Space>
          </Space>
        </Card>
      ) : null}

      {week && week.cards.length === 0 && !isFetching ? (
        <Typography.Paragraph type="secondary" data-testid="diary-hub-empty">
          {t('diaryCard.hub.empty')}
        </Typography.Paragraph>
      ) : null}
      {week?.cards.map(grid)}
    </Space>
  );
};
